import { Controller } from '@hotwired/stimulus'
import posthog from 'posthog-js'
import { beforeSend } from 'helpers/posthog/scrub'

const API_HOST = 'https://us.i.posthog.com'

// Mounted once per layout by ApplicationHelper#render_posthog, whose element also binds the actions:
// `posthog:capture@window` from posthog_event_controller.js emitters, and `turbo:load@document` /
// `turbo:before-visit@document` because Turbo Drive swaps the body without reloading, so the SDK's
// init-only $pageview would never re-fire on navigation. There is no `window.posthog` global.
export default class extends Controller {
  static values = {
    apiKey: String,
    eventsEnabled: Boolean,
    userId: String,
    email: String,
    personId: { type: String, default: 'none' },
    impersonatorId: { type: String, default: 'none' },
    impersonatorEmail: { type: String, default: 'none' }
  }

  connect() {
    if (!posthog.__loaded) posthog.init(this.apiKeyValue, this.config)
    this.identify()
  }

  capture({ detail: { name, properties } }) {
    this.captureIfEnabled(name, properties)
  }

  pageview() {
    this.captureIfEnabled('$pageview')
  }

  pageleave() {
    this.captureIfEnabled('$pageleave')
  }

  identify() {
    if (!this.userIdValue) {
      posthog.reset()
      return
    }

    posthog.identify(this.userIdValue, { email: this.emailValue })
    posthog.register({
      impersonator_email: this.impersonatorEmailValue,
      impersonator_id: this.impersonatorIdValue,
      person_id: this.personIdValue
    })
  }

  captureIfEnabled(...args) {
    if (!this.eventsEnabledValue) return

    posthog.capture(...args)
  }

  get config() {
    return {
      api_host: API_HOST,
      person_profiles: 'identified_only',
      autocapture: this.eventsEnabledValue,
      capture_pageview: false,
      capture_pageleave: this.eventsEnabledValue,
      before_send: beforeSend,
      session_recording: {
        maskTextSelector: '.ph-masked',
        maskInputFn: (text, element) => {
          if (element?.attributes['data-ph-masked']?.value === 'true') {
            return '*'.repeat(text.length)
          }
        }
      }
    }
  }
}
